<script setup>
  import { ref, watch, computed, defineEmits, defineProps, onMounted } from 'vue'

  // 最後に送信したデータの文字列表現
  let lastEmittedData = ''

  const props = defineProps({
    initialData: {
      type: Array,
      default: () => [],
    },
    // セレクトのオプション情報を受け取るプロパティを追加
    optionsMap: {
      type: Object,
      default: () => ({}),
    },
    // リフレッシュトリガープロパティを追加
    refreshTrigger: {
      type: Number,
      default: 0,
    },
    // 一括編集ボタンの表示制御
    showBulkEdit: {
      type: Boolean,
      default: true,
    },
    // 一括編集で除外するセクション名の配列
    excludeSections: {
      type: Array,
      default: () => [],
    },
    // 一括編集で除外するフィールド名の配列
    excludeFields: {
      type: Array,
      default: () => [],
    },
  })

  const emit = defineEmits(['save', 'bulkEditConfirm'])

  // 簡素化したデータ構造を作成する関数
  const createSimplifiedData = () => {
    return cloneValues(dataList.value).map(item => {
      return {
        label: item.label,
        value: item.value.map(subItem => {
          return {
            label: subItem.label,
            value: subItem.value.map(valueItem => {
              return {
                label: valueItem.label,
                value: valueItem.value,
              }
            }),
          }
        }),
      }
    })
  }

  const defaultValue = {
    label: '',
    value: '',
    type: 'text',
    placeholder: '',
    pattern: '',
    errorMessage: '',
    disabled: false,
    required: false,
    options: [],
    maxLength: null,
    minLength: null,
  }

  // 初期データ（デフォルト値を含まない）
  const defaultInitialData = [
    {
      label: '従業員情報',
      value: [
        {
          label: '本人情報',
          value: [
            {
              label: '姓名',
              value: ['矢島', '智隆'],
              type: 'name',
            },
            {
              label: '姓名（カタカナ）',
              value: ['ヤジマ', 'トモタカ'],
              type: 'name',
              pattern: '^[ァ-ヶー]+$',
              errorMessage: 'カタカナで入力してください',
            },
            {
              label: '入社日',
              value: '2023-01-01',
              type: 'date',
            },
            {
              label: '生年月日',
              value: '1990-01-01',
              type: 'date',
            },
            {
              label: '性別',
              value: '男',
              type: 'select',
              options: [
                { label: '男', value: '男' },
                { label: '女', value: '女' },
              ],
            },
            {
              label: '電話番号',
              value: ['090', '1234', '5678'],
              type: 'tel',
            },
            {
              label: 'メールアドレス',
              value: '',
              type: 'email',
              pattern: '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$',
              errorMessage: '正しいメールアドレスを入力してください',
            },
            {
              label: 'パスワード',
              value: 'password123',
              type: 'password',
              pattern: '^(?=.*[0-9])(?=.*[a-zA-Z])[0-9a-zA-Z]{8,16}$',
              errorMessage: '8文字以上の16文字以下の英数字を入力してください',
            },
            {
              label: '備考',
              value: '',
              type: 'textarea',
              placeholder: '備考を入力してください',
            },
            {
              label: '同意書',
              value: ['同意する'],
              type: 'checkbox',
              options: [
                { label: '同意する', value: '同意する' },
                { label: '個人情報の取り扱いに同意する', value: '個人情報の取り扱いに同意する' },
              ],
              required: true,
              errorMessage: '同意書に同意してください',
            },
            {
              label: '郵便番号',
              value: ['123', '4567'],
              type: 'zipcode',
            },
            {
              label: '雇用形態',
              value: '正社員',
              type: 'radio',
              options: [
                { label: '正社員', value: '正社員' },
                { label: '契約社員', value: '契約社員' },
                { label: 'パート・アルバイト', value: 'パート・アルバイト' },
              ],
            },
            {
              label: '希望勤務地',
              value: ['東京', '大阪'],
              type: 'checkbox',
              options: [
                { label: '東京', value: '東京' },
                { label: '大阪', value: '大阪' },
                { label: '名古屋', value: '名古屋' },
                { label: '福岡', value: '福岡' },
              ],
            },
          ],
        },
      ],
    },
  ]

  // 各データ項目にデフォルト値を適用する関数
  const applyDefaultValues = data => {
    // 深いコピーを作成
    const result = JSON.parse(JSON.stringify(data))

    // 再帰的に全てのデータ項目を処理
    const processItem = item => {
      // 配列の場合は各要素を再帰的に処理
      if (Array.isArray(item)) {
        return item.map(el => processItem(el))
      }

      // オブジェクトの場合
      if (item && typeof item === 'object') {
        // value配列を処理（フォーム項目の配列の場合）
        if (item.value && Array.isArray(item.value) && item.value.length > 0 && typeof item.value[0] === 'object') {
          item.value = processItem(item.value)
        }

        // デフォルト値を適用
        for (const key in defaultValue) {
          if (item[key] === undefined) {
            item[key] = JSON.parse(JSON.stringify(defaultValue[key]))
          }
        }
      }

      return item
    }

    return processItem(result)
  }

  // デフォルト値を適用してdataListを初期化
  const dataList = ref([])

  // セレクトオプションを更新するための関数
  const updateSelectOptions = () => {
    if (!props.optionsMap || Object.keys(props.optionsMap).length === 0) return

    // 全データを深さ優先で探索し、該当するフィールドのオプションを更新
    const updateOptionsRecursive = items => {
      if (!items || !Array.isArray(items)) return

      // nullではない項目のみを処理
      const validItems = items.filter(item => item !== null && item !== undefined)

      for (const item of validItems) {
        // セレクト/ラジオ/チェックボックスフィールドのオプション更新
        if (item.label && (item.type === 'select' || item.type === 'radio' || item.type === 'checkbox')) {
          // キーがあれば更新（キーはフィールドのラベルまたは指定した識別子）
          const key = item.optionsKey || item.label // optionsKeyがあればそれを使用

          if (props.optionsMap[key]) {
            // オプション形式に変換（必要に応じて）
            const newOptions = convertToOptionsFormat(props.optionsMap[key])
            item.options = newOptions
          }
        }

        // 子要素があれば再帰（nullチェック追加）
        if (item.value && Array.isArray(item.value) && item.value.length > 0) {
          // null以外の要素があるかチェック
          const hasObjects = item.value.some(v => v !== null && typeof v === 'object')
          if (hasObjects) {
            updateOptionsRecursive(item.value)
          }
        }
      }
    }

    // dataListに適用
    updateOptionsRecursive(dataList.value)
  }

  // オブジェクト形式をオプション配列に変換する補助関数
  const convertToOptionsFormat = options => {
    if (Array.isArray(options)) {
      // すでに[{label, value}]形式ならそのまま返す
      if (options.length > 0 && typeof options[0] === 'object' && 'label' in options[0]) {
        return options
      }
      // 文字列配列なら変換
      return options.map(opt => ({ label: opt, value: opt }))
    } else if (typeof options === 'object') {
      // {key: value}形式ならラベルと値のペアに変換
      return Object.entries(options).map(([value, label]) => ({ label, value }))
    }
    return []
  }

  // リセット関数 - データリストを初期化する
  const resetDataList = () => {
    // props.initialData が空の場合はデフォルトを使用
    const useDefault = !props.initialData || props.initialData.length === 0

    const sourceData = useDefault ? JSON.parse(JSON.stringify(defaultInitialData)) : JSON.parse(JSON.stringify(props.initialData))

    // saveイベントが発火しないようにwatchを一時的に無効化するフラグ
    isResetting = true

    try {
      dataList.value = applyDefaultValues(sourceData)
    } catch (error) {
      return
    }

    // オプションの初期適用
    updateSelectOptions()

    // 次のティックでフラグをリセット
    setTimeout(() => {
      isResetting = false
    }, 0)
  }

  // リセット中かどうかのフラグ
  let isResetting = false

  onMounted(() => {
    // 初期化時はリセットフラグを使って無限ループを防止
    isResetting = true

    resetDataList()

    // 次のティックでフラグをリセット
    setTimeout(() => {
      isResetting = false
    }, 0)
  })

  // optionsMapの変更を監視
  watch(
    () => props.optionsMap,
    () => {
      updateSelectOptions()
    },
    { deep: true }
  )

  // refreshTriggerの変更を監視して、変更があれば再初期化
  watch(
    () => props.refreshTrigger,
    () => {
      if (props.refreshTrigger > 0) {
        resetDataList()
        // 編集モードを終了
        showPopup.value = false
        editingItem.value = null
        editingValues.value = []
      }
    }
  )

  // dataListの変更を監視して更新イベントを発行
  watch(
    dataList,
    () => {
      // リセット中はsaveイベントを発火しない
      if (isResetting) return

      // props.refreshTriggerが0の場合（初期表示時）は発火しない
      if (props.refreshTrigger === 0) {
        return
      }

      // 前回のデータと実質的に同じ場合は発火しない（オプション）
      const currentData = JSON.stringify(createSimplifiedData())
      if (lastEmittedData === currentData) {
        return
      }

      // 現在のデータを記録
      lastEmittedData = currentData

      const simplifiedData = createSimplifiedData()
      // save イベントを発行
      emit('save', simplifiedData)
    },
    { deep: true }
  )

  // 編集中のデータを保持するための変数
  const editingItem = ref(null) // 編集中の項目
  const editingValues = ref([]) // 編集中の値
  const showPopup = ref(false) // ポップアップの表示状態

  // データの複製を行う関数
  const cloneValues = values => {
    return JSON.parse(JSON.stringify(values))
  }

  // 編集を開始する関数
  const startEdit = (item, index, subIndex) => {
    editingItem.value = { item, index, subIndex }
    editingValues.value = cloneValues(item.value)
    showPopup.value = true
  }

  // 編集をキャンセルする関数
  const cancelEdit = () => {
    editingItem.value = null
    editingValues.value = []
    showPopup.value = false
  }

  // 編集を確定する関数
  const confirmEdit = () => {
    if (editingItem.value) {
      if (editingItem.value.isAllEdit) {
        // 統合編集の場合、データを元の構造に戻す
        editingValues.value.forEach(item => {
          const { categoryIndex, subIndex, categoryLabel, subItemLabel, ...cleanItem } = item
          const targetItem = dataList.value[categoryIndex].value[subIndex].value.find(v => v.label === cleanItem.label)
          if (targetItem) {
            Object.assign(targetItem, cleanItem)
          }
        })

        // データ更新後にsaveイベントを手動で発行して親コンポーネントのデータを更新
        const simplifiedData = createSimplifiedData()
        emit('save', simplifiedData)

        // その後に一括編集確定時のイベントを発行
        emit('bulkEditConfirm')
      } else {
        // 既存の個別編集の処理
        const { item } = editingItem.value
        item.value = cloneValues(editingValues.value)
      }
    }
    showPopup.value = false
    editingItem.value = null
  }
  // チェックボックスの値を処理するヘルパー関数
  const isChecked = (value, option) => {
    if (Array.isArray(value.value)) {
      return value.value.includes(option.value)
    }
    return false
  }

  // チェックボックスの値を更新する関数
  const updateCheckboxValue = (value, option, checked) => {
    if (!Array.isArray(value.value)) {
      value.value = []
    }

    if (checked) {
      // 値が既に存在しない場合のみ追加
      if (!value.value.includes(option.value)) {
        value.value.push(option.value)
      }
    } else {
      // 値が存在する場合のみ削除
      value.value = value.value.filter(v => v !== option.value)
    }
  }

  // バリデーションチェック関数
  const hasError = value => {
    // 必須でない場合で値が空の場合はエラーなし
    if (!value.required && (!value.value || (Array.isArray(value.value) && value.value.length === 0) || value.value === '')) {
      return false
    }

    // チェックボックスで必須の場合、少なくとも1つ選択されている必要がある
    if (value.type === 'checkbox' && value.required && Array.isArray(value.value)) {
      return value.value.length === 0
    }

    // パターン検証がある場合
    if (value.pattern) {
      const regex = new RegExp(value.pattern)

      // 配列値（名前フィールドなど）の処理
      if (Array.isArray(value.value)) {
        // チェックボックスの場合はパターン検証をスキップ
        if (value.type === 'checkbox') {
          return false
        }

        // 必須でないフィールドは、入力された値のみを検証
        if (!value.required) {
          return value.value.some(v => v && !regex.test(v))
        }
        return value.value.some(v => !regex.test(v))
      }

      // 通常の文字列値の処理
      return !regex.test(value.value)
    }

    return false
  }

  // 編集中の値にエラーがあるかをチェック
  const hasErrorInEditingValues = computed(() => {
    return editingValues.value.some(value => hasError(value))
  })

  // 指定した要素にスクロールする関数
  const scrollTo = index => {
    const element = document.getElementById(`a${index}`)
    if (element) {
      // コンテナを取得（.rightクラスの要素）
      const container = document.querySelector('.right')
      if (container) {
        // 要素の位置を計算
        const elementRect = element.getBoundingClientRect()
        const containerRect = container.getBoundingClientRect()

        // コンテナ内での相対位置を計算
        const relativeTop = elementRect.top - containerRect.top + container.scrollTop

        // スクロール位置を設定
        container.scrollTo({
          top: relativeTop,
          behavior: 'smooth',
        })
      } else {
        // コンテナが見つからない場合は従来の方法を使用
        element.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }
  }

  const startEditAll = () => {
    // すべてのデータを統合（指定されたセクションやフィールドを除外）
    const allData = []
    dataList.value.forEach((category, categoryIndex) => {
      category.value.forEach((subItem, subIndex) => {
        // 除外対象のセクションをスキップ
        if (props.excludeSections.some(excludeSection => subItem.label && subItem.label.includes(excludeSection))) {
          return
        }

        subItem.value.forEach(item => {
          // 除外対象のフィールドをスキップ
          if (props.excludeFields.some(excludeField => item.label && item.label.includes(excludeField))) {
            return
          }

          allData.push({
            ...item,
            categoryLabel: category.label,
            subItemLabel: subItem.label,
            categoryIndex,
            subIndex,
          })
        })
      })
    })

    editingItem.value = { isAllEdit: true }
    editingValues.value = cloneValues(allData)
    showPopup.value = true
  }

  // メソッドを外部に公開
  defineExpose({
    startEditAll,
  })
</script>

<template>
  <div class="data-editor">
    <!-- 左側のメニュー -->
    <div class="left">
      <!-- <div class="ttl">
                メンバーログイン情報
            </div> -->
      <div class="menu">
        <details open v-for="(item, index) in dataList" :key="index">
          <summary tabindex="-1">{{ item.label }}</summary>
          <ul>
            <li v-for="(subItem, subIndex) in item.value" :key="subIndex" @click="scrollTo(subIndex)">
              {{ subItem.label }}
            </li>
          </ul>
        </details>
      </div>
    </div>

    <!-- 右側のコンテンツ -->
    <div class="right scroller">
      <!-- 新規追加: すべて編集ボタン -->
      <div class="all-edit-area" v-if="props.showBulkEdit">
        <button class="btn all-edit" @click="startEditAll">
          <span class="material-symbols-outlined">edit_note</span>
          一括編集
        </button>
      </div>
      <template v-for="(item, index) in dataList" :key="index">
        <div class="area" v-for="(subItem, subIndex) in item.value" :key="subIndex" :id="`a${subIndex}`">
          <div class="ttl-edit">
            <div class="ttl">
              {{ subItem.label }}
            </div>
            <div class="btns">
              <button class="btn" @click="startEdit(subItem, index, subIndex)">
                <span class="material-symbols-outlined">edit</span>
                編集
              </button>
            </div>
          </div>
          <div class="values">
            <div class="item" v-for="(value, valueIndex) in subItem.value" :key="valueIndex">
              <div class="label">
                {{ value.label }}
              </div>
              <div class="value">
                <div class="value-text">
                  <template v-if="['text', 'email', 'date'].includes(value.type)">
                    <div class="value-text" v-text="value.value"></div>
                  </template>
                  <template v-else-if="value.type === 'password'">
                    <div class="value-text" v-text="value.value.replace(/./g, '*')"></div>
                  </template>
                  <template v-else-if="value.type === 'select'">
                    <div class="value-text" v-text="value.options.find(option => option.value === value.value)?.label"></div>
                  </template>
                  <template v-else-if="value.type === 'checkbox'">
                    <div class="value-text">
                      <div v-if="Array.isArray(value.value) && value.value.length > 0">
                        {{
                          value.value
                            .map(val => {
                              const option = value.options.find(opt => opt.value === val)
                              return option ? option.label : val
                            })
                            .join(', ')
                        }}
                      </div>
                      <div v-else>未選択</div>
                    </div>
                  </template>
                  <template v-else-if="value.type === 'radio'">
                    <div class="value-text" v-text="value.options.find(option => option.value === value.value)?.label"></div>
                  </template>
                  <template v-else-if="value.type === 'textarea'">
                    <div class="value-text" v-text="value.value || '(未入力)'"></div>
                  </template>
                  <template v-else-if="value.type === 'tel'">
                    <div class="value-text">
                      <div class="tel">
                        <span v-text="value.value.join('-')"></span>
                      </div>
                    </div>
                  </template>
                  <template v-else-if="value.type === 'name'">
                    <div class="value-text">
                      <div class="name">
                        <span v-text="value.value.join(' ')"></span>
                      </div>
                    </div>
                  </template>
                  <template v-else-if="value.type === 'zipcode'">
                    <div class="value-text">
                      <div class="zipcode">
                        <span v-text="value.value.join('-')"></span>
                      </div>
                    </div>
                  </template>
                  <template v-else>
                    <div class="value-text">Unknown type: {{ value.type }}</div>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- 編集用ポップアップ -->
    <div class="popup-overlay" v-if="showPopup" @click.self="cancelEdit">
      <div class="popup">
        <button class="close-btn" @click="cancelEdit">
          <span class="material-symbols-outlined">close</span>
        </button>
        <div class="popup-header">
          <div class="popup-title">
            {{ editingItem?.isAllEdit ? 'すべての項目を編集' : `${editingItem?.item?.label} の編集` }}
          </div>
        </div>
        <div class="popup-content scroller">
          <template v-for="(value, valueIndex) in editingValues" :key="valueIndex">
            <!-- 統合編集時のグループヘッダー -->
            <div v-if="editingItem?.isAllEdit && (valueIndex === 0 || value.categoryLabel !== editingValues[valueIndex - 1].categoryLabel || value.subItemLabel !== editingValues[valueIndex - 1].subItemLabel)" class="group-header">{{ value.categoryLabel }} > {{ value.subItemLabel }}</div>

            <!-- 共通の入力フィールド -->
            <div class="item" :class="{ 'in-group': editingItem?.isAllEdit }">
              <div class="label">
                {{ value.label }}
              </div>
              <div class="value">
                <template v-if="['text', 'email', 'password', 'number', 'date'].includes(value.type)">
                  <div class="input-area">
                    <input v-model="value.value" :type="value.type" :placeholder="value.placeholder" :pattern="value.pattern" :disabled="value.disabled" :required="value.required" :maxlength="value.maxLength" :minlength="value.minLength" />
                  </div>
                </template>
                <template v-else-if="value.type === 'select'">
                  <div class="select-area">
                    <div class="select">
                      <select v-model="value.value" :disabled="value.disabled">
                        <option v-for="(option, optionIndex) in value.options" :key="optionIndex" :value="option.value">
                          {{ option.label }}
                        </option>
                      </select>
                    </div>
                  </div>
                </template>
                <template v-else-if="value.type === 'checkbox'">
                  <div class="checkbox-area">
                    <div class="checkbox-group">
                      <div v-for="(option, optionIndex) in value.options" :key="optionIndex" class="checkbox">
                        <label>
                          <input type="checkbox" :checked="isChecked(value, option)" @change="e => updateCheckboxValue(value, option, e.target.checked)" :disabled="value.disabled" />
                          {{ option.label }}
                        </label>
                      </div>
                    </div>
                  </div>
                </template>
                <template v-else-if="value.type === 'radio'">
                  <div class="radio-area">
                    <div class="radio-group">
                      <div v-for="(option, optionIndex) in value.options" :key="optionIndex" class="radio">
                        <label>
                          <input type="radio" :value="option.value" v-model="value.value" :disabled="value.disabled" :name="`radio-${value.label}`" />
                          {{ option.label }}
                        </label>
                      </div>
                    </div>
                  </div>
                </template>
                <template v-else-if="value.type === 'textarea'">
                  <div class="textarea-area">
                    <textarea v-model="value.value" :placeholder="value.placeholder" :disabled="value.disabled" :required="value.required" :maxlength="value.maxLength" :minlength="value.minLength"></textarea>
                  </div>
                </template>
                <template v-else-if="value.type === 'tel'">
                  <div class="input-area">
                    <input v-model="value.value[0]" type="text" :placeholder="value.placeholder" :disabled="value.disabled" :required="value.required" :maxlength="value.maxLength" :minlength="value.minLength" />
                    -
                    <input v-model="value.value[1]" type="text" :placeholder="value.placeholder" :disabled="value.disabled" :required="value.required" :maxlength="value.maxLength" :minlength="value.minLength" />
                    -
                    <input v-model="value.value[2]" type="text" :placeholder="value.placeholder" :disabled="value.disabled" :required="value.required" :maxlength="value.maxLength" :minlength="value.minLength" />
                  </div>
                </template>
                <template v-else-if="value.type === 'name'">
                  <div class="input-area">
                    <input v-model="value.value[0]" type="text" :placeholder="value.placeholder" :disabled="value.disabled" :required="value.required" :maxlength="value.maxLength" :minlength="value.minLength" />
                    <input v-model="value.value[1]" type="text" :placeholder="value.placeholder" :disabled="value.disabled" :required="value.required" :maxlength="value.maxLength" :minlength="value.minLength" />
                  </div>
                </template>
                <template v-else-if="value.type === 'zipcode'">
                  <div class="input-area">
                    <input v-model="value.value[0]" type="text" :placeholder="value.placeholder" :disabled="value.disabled" :required="value.required" :maxlength="3" :minlength="value.minLength" />
                    -
                    <input v-model="value.value[1]" type="text" :placeholder="value.placeholder" :disabled="value.disabled" :required="value.required" :maxlength="4" :minlength="value.minLength" />
                  </div>
                </template>
                <template v-else>
                  <div class="unknown-type">Unknown type: {{ value.type }}</div>
                </template>
                <div v-if="value.errorMessage && hasError(value)" class="error-message">
                  {{ value.errorMessage }}
                </div>
              </div>
            </div>
          </template>
        </div>
        <div class="popup-footer">
          <button class="btn cancel" @click="cancelEdit">キャンセル</button>
          <button class="btn" @click="confirmEdit" :disabled="hasErrorInEditingValues">確定</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="less" scoped>
  .data-editor {
    display: grid;
    grid-template-columns: 220px 1fr;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow-y: auto;
  }

  .left {
    padding: 0 30px;
    display: flex;
    flex-direction: column;
    gap: 30px;
    align-items: start;

    .ttl {
      font-size: 16px;
    }

    .menu {
      display: grid;
      gap: 30px;
      width: 100%;

      details {
        display: flex;
        flex-direction: column;
        gap: 20px;
        width: 100%;

        summary {
          display: flex;
          width: 100%;
          justify-content: space-between;
          font-size: 15px;
          cursor: pointer;

          &::-webkit-details-marker {
            display: none;
          }

          &::after {
            content: 'keyboard_arrow_down';
            font-family: 'Material Symbols Outlined';
            display: inline-block;
            transition: transform 0.4s;
            font-size: 15px;
          }
        }

        &[open] {
          summary {
            &::after {
              transform: rotate(180deg);
            }
          }
        }

        ul {
          font-size: 14px;
          display: flex;
          flex-direction: column;
          gap: 20px;

          li {
            cursor: pointer;
            transition: opacity 0.6s;
            padding-left: 1em;

            &:hover {
              opacity: 0.7;
            }
          }
        }
      }
    }
  }

  .right {
    padding: 0 20px;
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding-bottom: 20px;
    overflow-y: auto;

    .area {
      background-color: #fff;
      border-radius: 20px;
      padding: 20px;
      box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);

      .ttl-edit {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0;

        .ttl {
          font-size: 16px;
        }

        .btns {
          display: flex;
          gap: 10px;
        }

        .btn {
          display: flex;
          align-items: center;
          gap: 5px;
          font-size: 13px;
          background-color: var(--sub-color);
          color: #fff;
          width: max-content;
          padding: 5px 10px;
          height: 25px;
          display: flex;
          justify-content: center;
          align-items: center;
          border-radius: 15px;

          span {
            font-size: 20px;
          }

          transition: opacity 0.6s;

          &:hover {
            opacity: 0.7;
          }

          &.cancel {
            background-color: #f44336;
          }

          &:disabled {
            background-color: #e0e0e0;
            cursor: not-allowed;
          }
        }
      }

      .values {
        padding-left: 1em;

        .item {
          display: grid;
          grid-template-columns: 200px 1fr;
          gap: 20px;
          align-items: center;
          padding: 10px 0;
          line-height: 1.4;

          .label {
            font-size: 14px;
          }

          .value {
            font-size: 14px;
          }
        }
      }
    }
  }

  /* ポップアップ関連のスタイル */
  .popup-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
    padding: 0 20px;
  }

  .popup {
    background-color: white;
    border-radius: 20px 20px 0px 0px;
    width: 100%;
    max-width: 640px;
    max-height: calc(100vh - 40px);
    display: flex;
    flex-direction: column;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
    position: relative;
  }

  .close-btn {
    position: absolute;
    color: #fff;
    background-color: var(--active-color);
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    right: -18px;
    top: -18px;
    transition: opacity 0.3s;
  }

  .popup-header {
    border-radius: 15px 15px 0px 0px;
    padding: 30px 35px;
    background-color: #e0f0e8;
    color: var(--active-color);
    font-size: 18px;
    font-weight: bold;
    display: flex;
    align-items: center;
    gap: 10px;
    letter-spacing: 0.1em;

    .popup-title {
      font-size: 18px;
      font-weight: bold;
    }
  }

  .popup-content {
    padding: 30px 35px;
    overflow-y: auto;
    flex-grow: 1;

    .item {
      display: grid;
      grid-template-columns: 90px 1fr;
      gap: 20px;
      margin-bottom: 15px;
      align-items: center;

      .label {
        font-size: 12px;
        text-align: right;
      }

      .value {
        font-size: 14px;
      }

      .input-area {
        display: flex;
        gap: 10px;
        align-items: center;
      }

      input[type='text'],
      input[type='tel'],
      input[type='number'],
      input[type='email'],
      input[type='password'],
      input[type='date'],
      textarea,
      .select {
        font-size: 14px;
        padding: 8px 12px;
        background-color: #f7f7f7;
        border: 1px solid #898989;
        border-radius: 3px;

        &:disabled {
          background-color: #e0e0e0;
          cursor: not-allowed;
        }
      }

      input[type='text'],
      input[type='tel'],
      input[type='email'],
      input[type='password'],
      input[type='number'],
      input[type='date'],
      textarea {
        width: 100%;
      }

      .select {
        display: inline-flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
        padding: 0;
        position: relative;
        width: 100%;

        select {
          padding: 8px 12px;
          width: 100%;

          &:disabled {
            background-color: #e0e0e0;
            cursor: not-allowed;
          }
        }

        &::after {
          content: '▼';
          color: #898989;
          position: absolute;
          right: 10px;
          pointer-events: none;
          background-color: #f7f7f7;
        }

        &:has(select:disabled) {
          &::after {
            background-color: #e0e0e0;
          }
        }
      }

      .checkbox-group {
        display: flex;
        flex-direction: column;
        gap: 10px;

        .checkbox {
          display: flex;
          align-items: center;

          label {
            display: inline-flex;
            align-items: center;
            gap: 5px;
            height: 100%;
            font-size: 14px;
            cursor: pointer;

            input[type='checkbox'] {
              width: 18px;
              aspect-ratio: 1;
              background-color: #f7f7f7;
              border: 1px solid #898989;
              cursor: pointer;
              position: relative;

              &:checked {
                &::after {
                  content: '';
                  display: block;
                  width: 14px;
                  aspect-ratio: 1;
                  background-color: var(--enabled-color, #4285f4);
                  position: absolute;
                  top: 50%;
                  left: 50%;
                  transform: translate(-50%, -50%);
                }
              }
            }
          }
        }
      }

      .radio-group {
        display: flex;
        flex-direction: column;
        gap: 10px;

        .radio {
          label {
            display: inline-flex;
            align-items: center;
            gap: 5px;
            font-size: 14px;
            cursor: pointer;
            user-select: none;

            input[type='radio'] {
              width: 18px;
              height: 18px;
              appearance: none;
              background-color: #f7f7f7;
              border: 1px solid #898989;
              border-radius: 50%;
              cursor: pointer;
              position: relative;

              &:checked {
                &::after {
                  content: '';
                  display: block;
                  width: 10px;
                  height: 10px;
                  background-color: var(--enabled-color, #4285f4);
                  border-radius: 50%;
                  position: absolute;
                  top: 50%;
                  left: 50%;
                  transform: translate(-50%, -50%);
                }
              }

              &:disabled {
                background-color: #e0e0e0;
                cursor: not-allowed;
              }
            }
          }
        }
      }

      .error-message {
        color: red;
        font-size: 12px;
        margin-top: 5px;
      }
    }
  }

  .popup-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    background-color: #f5f5f5;
    padding: 10px 35px;

    .btn {
      border-radius: 11px;
      font-size: 12px;
      font-weight: bold;
      min-width: 68px;
      height: 22px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: opacity 0.3s;
      white-space: nowrap;
      padding: 0 15px;
      color: #fff;
      background-color: var(--sub-color);

      &.cancel {
        color: #fff;
        background-color: var(--alert-color);
      }
      &:hover {
        opacity: 0.7;
      }
    }
  }

  /* レスポンシブ対応 */
  @media (max-width: 768px) {
    // .data-editor {
    //   grid-template-columns: 1fr;
    // }

    // .left {
    //   padding: 20px;
    // }

    // .popup {
    //   width: 95%;
    // }

    // .popup-content .item {
    //   grid-template-columns: 1fr;
    //   gap: 5px;
    // }
  }

  .all-edit-area {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 10px;

    .btn.all-edit {
      display: flex;
      align-items: center;
      color: #fff;
      background-color: var(--sub-color);
      padding: 8px 16px;
      height: 36px;
      font-size: 14px;
      border-radius: 18px;
      gap: 5px;

      span {
        font-size: 24px;
      }
    }
  }

  .group-header {
    font-size: 13px;
    color: var(--active-color);
    margin-bottom: 15px;
    padding: 5px 10px;
    background-color: #f5f5f5;
    border-radius: 5px;
  }
</style>
